import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {
  ApiMode,
  CallbackPayload,
  FdkTheme,
  LighthouseBrandListDisplayItem,
  LighthouseEndpointListDisplayItem,
  LighthousePortalListDisplayItem,
  RecordLocatorFacility, RecordLocatorFacilityConnected,
  SDKMode,
  SearchConfig, SourceCredentialType,
  SystemConfig,
  VaultProfileConfig
} from '@shared-library';
import {merge, update} from 'lodash';
import {NGXLogger} from 'ngx-logger';
import {v4 as uuidv4} from "uuid";

/**
 * Default configuration for the entire application. This object is used by
 * "ConfigService" to set the default configuration.
 */
export const defaultSystemConfig: SystemConfig = {
  publicId: '',
  apiMode: ApiMode.Test,
  sdkMode: SDKMode.None,
  theme: FdkTheme.Light,
};

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  //TODO: there should be 3 types of "config" settings:
  // 1. System settings
  // - apiMode
  // - organization information
  // - org credential information
  // - reconnect id.
  // 2. VaultProfile/User settings
  // - user token/cookie data
  // - vault profile
  // - current connected patient auth's
  // - (FUTURE) potential patient auths?
  // 3. Search settings
  // - search filters
  // - selected brand
  // -

  //public but should only be used in HTML directly. (via `| async`)
  public systemConfigSubject: BehaviorSubject<SystemConfig>;
  public vaultProfileConfigSubject: BehaviorSubject<VaultProfileConfig>;
  public searchConfigSubject: BehaviorSubject<SearchConfig>;


  constructor(
    private logger: NGXLogger,
  ) {
    this.systemConfigSubject = new BehaviorSubject(defaultSystemConfig);
    this.vaultProfileConfigSubject = new BehaviorSubject(Object.setPrototypeOf({}, VaultProfileConfig.prototype));
    this.searchConfigSubject = new BehaviorSubject({} as SearchConfig);

  }


  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for config
   */

  //Setter
  set systemConfig(value: Partial<SystemConfig>) {
    // Treat each top-level SystemConfig value as a complete snapshot. A shallow
    // update lets callers explicitly clear optional fields and avoids combining
    // organization data or arrays from two different configurations.
    const currentSettings = this.systemConfigSubject.getValue();
    const mergedSettings = {...currentSettings, ...value};

    // Do not retain an organization from a previously loaded tenant.
    if (!Object.prototype.hasOwnProperty.call(value, 'org')
      && value.publicId !== undefined
      && value.publicId !== currentSettings.publicId) {
      mergedSettings.org = undefined;
    }

    // Store the value
    if(JSON.stringify(mergedSettings) !== JSON.stringify(currentSettings)){
      this.systemConfigSubject.next(mergedSettings);
      // BehaviorSubject publishes synchronously, so telemetry subscribers now
      // have the new context before this log is captured.
      this.logger.info('updating system settings:', mergedSettings)
    }
  }

  //Getter
  get systemConfig$(): SystemConfig {
    // this.logger.info('getting cached system settings:', this.systemConfigSubject.getValue())
    return this.systemConfigSubject.getValue()
  }

  //Setter
  set vaultProfileConfig(value: Partial<VaultProfileConfig>) {
    // get the current config, merge the new values, and then submit.
    const mergedSettings = merge({}, this.vaultProfileConfigSubject.getValue(), value);

    // Store the value
    if(JSON.stringify(mergedSettings) !== JSON.stringify(this.vaultProfileConfigSubject.getValue())){
      this.logger.info('updating vault profile settings:', mergedSettings)
      this.vaultProfileConfigSubject.next(Object.setPrototypeOf(mergedSettings, VaultProfileConfig.prototype));
    }
  }
  //Getter
  get vaultProfileConfig$(): VaultProfileConfig {
    this.logger.info('getting vault profile settings:', this.vaultProfileConfigSubject.getValue())
    return this.vaultProfileConfigSubject.getValue()
  }
  vaultProfileAddPendingAccount(brand: LighthouseBrandListDisplayItem, portal: LighthousePortalListDisplayItem, endpoint: LighthouseEndpointListDisplayItem){
    let updatedVaultProfile = this.vaultProfileConfig$
    let externalState = uuidv4(); //generate a unique external state for this connection
    updatedVaultProfile.addPendingAccount(externalState, brand, portal, endpoint)
    this.vaultProfileConfig = updatedVaultProfile
  }
  vaultProfileAddConnectedAccount(connectedAccount:  CallbackPayload){
    if(connectedAccount.error){
      this.logger.error("Error connecting account", connectedAccount)
      return
    }
    let updatedVaultProfile = this.vaultProfileConfig$
    updatedVaultProfile.addConnectedAccount(
      connectedAccount.vault_profile_connection_id || connectedAccount.external_state || "",
      connectedAccount.org_connection_id!,
      connectedAccount.connection_status!,
      connectedAccount.platform_type!,
      connectedAccount.brand_id,
      connectedAccount.portal_id,
      connectedAccount.endpoint_id,
      connectedAccount.vault_profile_connection_id,
      connectedAccount.patient_auth_type,
      connectedAccount.scope,
      connectedAccount.consent_expires_at,
      connectedAccount.tefca_directory_id,
      connectedAccount.display_name,
    )
    this.vaultProfileConfig = updatedVaultProfile
  }

  // this is used when we successfully generated an org_connection_id for a TEFCA Direct account.
  vaultProfileAuthorizeTefcaDirectConnectedAccount(vaultProfileConnectionId: string, orgConnectionId: string, connectionStatus: string, tefcaDirectoryId?: string){
    let updatedVaultProfile = this.vaultProfileConfig$
    let isUpdated = updatedVaultProfile.authorizeTefcaDirectConnectedAccount(vaultProfileConnectionId, orgConnectionId, connectionStatus, tefcaDirectoryId)
    if (!isUpdated) {
      this.logger.warn("Could not find TEFCA Direct account by connection id:", vaultProfileConnectionId)
      return
    }
    this.vaultProfileConfig = updatedVaultProfile
  }

  //this is used when we were unable to generate an org_connection_id for a TEFCA Direct account.
  vaultProfileRevokeTefcaDirectConnectedAccount(vaultProfileConnectionId: string){
    let updatedVaultProfile = this.vaultProfileConfig$
    let isUpdated = updatedVaultProfile.revokeTefcaDirectConnectedAccount(vaultProfileConnectionId)
    if (!isUpdated) {
      this.logger.warn("Could not find TEFCA Direct account by connection id:", vaultProfileConnectionId)
      return
    }
    this.vaultProfileConfig = updatedVaultProfile
  }

  vaultProfileDeselectTefcaDirectConnectedAccount(vaultProfileConnectionId: string){
    let updatedVaultProfile = this.vaultProfileConfig$
    let isUpdated = updatedVaultProfile.deselectTefcaDirectConnectedAccount(vaultProfileConnectionId)
    if (!isUpdated) {
      this.logger.warn("Could not find TEFCA Direct account by connection id:", vaultProfileConnectionId)
      return
    }
    this.vaultProfileConfig = updatedVaultProfile
  }

  vaultProfileAddPendingRecordLocatorAccount(recordLocatorFacility: RecordLocatorFacility, vaultProfileConnectionId: string){
    let updatedVaultProfile = this.vaultProfileConfig$
    let externalState = vaultProfileConnectionId; //no need to generate a unique external state for this connection, the backend already has an identifier: the vault profile connection id.
    updatedVaultProfile.addPendingAccount(externalState, recordLocatorFacility.brand, recordLocatorFacility.portal, recordLocatorFacility.endpoint, vaultProfileConnectionId)
    this.vaultProfileConfig = updatedVaultProfile
  }
  vaultProfileAddDiscoveredRecordLocatorAccount(recordLocatorFacility: RecordLocatorFacility, vaultProfileConnectionId: string){
    let updatedVaultProfile = this.vaultProfileConfig$
    updatedVaultProfile.addDiscoveredAccount(recordLocatorFacility.brand, recordLocatorFacility.portal, recordLocatorFacility.endpoint, vaultProfileConnectionId, recordLocatorFacility.patient_authorization_type)
    this.vaultProfileConfig = updatedVaultProfile
  }

  //this can only be used after the RLS account has been previously connected
  vaultProfileAddConnectedRecordLocatorAccount(recordLocatorFacility: RecordLocatorFacilityConnected, vaultProfileConnectionId: string){
    let updatedVaultProfile = this.vaultProfileConfig$
    updatedVaultProfile.addConnectedRecordLocatorAccount(
      recordLocatorFacility,
      vaultProfileConnectionId,
    )
    this.vaultProfileConfig = updatedVaultProfile
  }


  //Setter
  set searchConfig(value: Partial<SearchConfig>) {
    // get the current config, merge the new values, and then submit.
    const mergedSettings = merge({}, this.searchConfigSubject.getValue(), value);

    // Store the value
    if(JSON.stringify(mergedSettings) !== JSON.stringify(this.searchConfigSubject.getValue())){
      this.logger.info('updating search settings:', mergedSettings)
      this.searchConfigSubject.next(mergedSettings);
    }
  }
  //Getter
  get searchConfig$(): SearchConfig {
    this.logger.info('getting search settings:', this.searchConfigSubject.getValue())
    return this.searchConfigSubject.getValue()
  }
}
